import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Gift, X, Trophy } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, resolveImageUrl } from "../lib/config";

export default function PrizesGalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/prizes")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-fp-bg" data-testid="prizes-gallery-page">
      <Navbar />

      <section className="relative pt-32 pb-16 px-5 md:px-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-fp-cyan text-sm mb-8 transition-colors" data-testid="back-home-btn">
            <ArrowLeft size={16} /> Torna alla home
          </Link>

          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
              <Trophy size={14} /> Il montepremi in vetrina
            </div>
            <h1 className="mt-4 font-heading font-black text-4xl md:text-6xl lg:text-7xl tracking-tight">
              <span className="silver-gradient">Tutti i</span>{" "}
              <span className="cyan-gradient">Premi</span>
            </h1>
            <p className="mt-5 text-white/60 max-w-2xl mx-auto">
              Oltre ai premi in denaro, scopri i riconoscimenti, i trofei e i premi tematici delle competizioni collaterali.
            </p>
          </div>

          {loading ? (
            <div className="text-center text-white/50 py-20" data-testid="prizes-loading">Caricamento...</div>
          ) : items.length === 0 ? (
            <div className="max-w-xl mx-auto text-center py-20 px-6 rounded-3xl border border-white/10 bg-fp-surface/40" data-testid="prizes-empty">
              <Gift size={40} className="mx-auto text-white/30 mb-3" />
              <div className="text-white/60">Nessun premio ancora pubblicato.</div>
              <div className="text-xs text-white/40 mt-2">Torna presto — i premi verranno presto svelati.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="prizes-grid">
              {items.map((p, i) => (
                <motion.button
                  key={p.id}
                  onClick={() => setLightbox(p)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: (i % 6) * 0.05 }}
                  className="group relative text-left rounded-2xl overflow-hidden border border-white/10 bg-fp-surface/40 hover:border-fp-cyan/40 transition-all"
                  data-testid={`prize-card-${p.id}`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-fp-bg">
                    <img
                      src={resolveImageUrl(p.image_url)}
                      alt={p.title || `Premio ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  {(p.title || p.subtitle) && (
                    <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-fp-bg/95 via-fp-bg/60 to-transparent px-5 py-4">
                      {p.title && (
                        <div className="font-heading font-bold text-white text-lg md:text-xl drop-shadow">
                          {p.title}
                        </div>
                      )}
                      {p.subtitle && (
                        <div className="mt-0.5 text-xs md:text-sm text-white/70 line-clamp-2">
                          {p.subtitle}
                        </div>
                      )}
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-fp-bg/95 backdrop-blur-md flex items-center justify-center p-5 md:p-10"
            onClick={() => setLightbox(null)}
            data-testid="prizes-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              data-testid="lightbox-close-btn"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-5xl max-h-[85vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={resolveImageUrl(lightbox.image_url)}
                alt={lightbox.title || "Premio"}
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
              />
              {(lightbox.title || lightbox.subtitle) && (
                <div className="mt-4 text-center">
                  {lightbox.title && <div className="font-heading font-bold text-white text-2xl md:text-3xl">{lightbox.title}</div>}
                  {lightbox.subtitle && <div className="mt-1 text-white/70 text-sm md:text-base max-w-2xl mx-auto">{lightbox.subtitle}</div>}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
